/**
 * AppShell — TV-RV-06
 *
 * Top-bar shell for TeleVisit. Reads the real authenticated user from useAuth()
 * and filters the phase ribbon by VIEW_CONFIG[user.role].phases.
 *
 * Changes from pre-slice1:
 *   - Deleted the hardcoded `Dr. Sarah Chen` mock user.
 *   - Deleted the `console.log('Toggle role')` stub.
 *   - Phase ribbon is driven by VIEW_CONFIG (role-aware) intersected with
 *     lite-mode restrictions (uiMode stays orthogonal).
 *   - Each PHASE now has a stable `key` (string slug) used by VIEW_CONFIG.
 *   - Role chip is read-only in prod; a dev-only role switcher renders
 *     behind `import.meta.env.DEV` for smoke testing.
 *   - The sessionStore.currentEncounter replaces the hardcoded mockEncounterId
 *     (falls back to 'enc-001' only in dev bypass).
 */

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Users } from 'lucide-react';
import Avatar from '../components/Avatar';
import { useAuth } from '../auth/AuthProvider';
import { getViewConfig } from '../auth/viewConfig';
import { ROLES } from '../auth/resolveRole';
import { useUiModeFlags } from '@/auth/useUiMode';
import { useSessionStore } from '../stores/sessionStore';

/**
 * All phases in display order. Each has:
 *   id     — legacy numeric id (still used for isActive/isCompleted logic)
 *   key    — stable string slug matched against VIEW_CONFIG[role].phases
 *   label  — display text
 *   route  — base path
 *   match  — prefix for route matching (startsWith)
 */
const PHASES = [
  { id: 1,  key: 'queue',        label: 'Queue',    route: '/queue',        match: '/queue' },
  { id: 2,  key: 'waiting-room', label: 'Wait',     route: '/waiting-room', match: '/waiting-room' },
  { id: 3,  key: 'visit',        label: 'Visit',    route: '/visit',        match: '/visit' },
  { id: 4,  key: 'assessment',   label: 'Assess',   route: '/assessment',   match: '/assessment' },
  { id: 5,  key: 'careplan',     label: 'Plan',     route: '/careplan',     match: '/careplan' },
  { id: 6,  key: 'ai-docs',      label: 'AI Notes', route: '/ai-docs',      match: '/ai-docs' },
  { id: 7,  key: 'ai-agent',     label: 'AI Agent', route: '/ai-agent',     match: '/ai-agent' },
  { id: 8,  key: 'billing',      label: 'Billing',  route: '/billing',      match: '/billing' },
  { id: 9,  key: 'summary',      label: 'Summary',  route: '/summary',      match: '/summary' },
  // 'schedule' is Slice 2 (TV-RV-11); it's in VIEW_CONFIG front-desk phases
  // but there's no route yet. AppShell filters it out naturally because no
  // PHASES entry carries key='schedule' — front-desk's ribbon just shows
  // the remaining 4 phases until the route is added.
];

// TV-LM-4 — Lite-mode: only Queue / Visit / Summary visible.
const LITE_PHASE_KEYS = new Set(['queue', 'visit', 'summary']);

// Encounter-specific routes need the active encounterId appended.
const ENCOUNTER_ROUTES = new Set(['/visit', '/assessment', '/careplan', '/ai-docs', '/ai-agent', '/billing', '/summary', '/sign']);

// Human-readable role label map.
const ROLE_LABELS = {
  [ROLES.FRONT_DESK]: 'Front Desk',
  [ROLES.PROVIDER]:   'Provider',
  [ROLES.PATIENT]:    'Patient',
  [ROLES.ADMIN]:      'Admin',
};

// ── Dev role switcher ────────────────────────────────────────────────────────
// Only rendered when import.meta.env.DEV is true. Allows smoke testing all
// three role views without a live KC token. Hidden entirely in prod builds.
const DEV_ROLES = [ROLES.FRONT_DESK, ROLES.PROVIDER, ROLES.PATIENT, ROLES.ADMIN];

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isLite } = useUiModeFlags();

  // TV-RV-06 — Read the real authenticated user.
  const { user, authMode } = useAuth();
  const currentEncounter = useSessionStore((s) => s.currentEncounter);

  // Dev role override (never affects prod).
  const [devRoleOverride, setDevRoleOverride] = useState(null);

  const effectiveRole = (import.meta.env.DEV && devRoleOverride) ? devRoleOverride : (user?.role ?? ROLES.PATIENT);
  const cfg = getViewConfig(effectiveRole);

  // Build the visible phase ribbon:
  //   1. Filter PHASES to those whose key is in cfg.phases (role-aware).
  //   2. If lite mode, further restrict to LITE_PHASE_KEYS.
  let visiblePhases = PHASES.filter((p) => cfg.phases.includes(p.key));
  if (isLite) {
    visiblePhases = visiblePhases.filter((p) => LITE_PHASE_KEYS.has(p.key));
  }

  // Determine current phase based on route.
  const getCurrentPhaseId = () => {
    const phase = PHASES.find((p) => currentPath.startsWith(p.match));
    return phase?.id || 1;
  };
  const currentPhaseId = getCurrentPhaseId();

  // Encounter-specific routes need the active encounterId appended.
  // Fall back to 'enc-001' only in dev bypass (no real session).
  const encounterId = currentEncounter || (import.meta.env.DEV ? 'enc-001' : null);

  const handlePhaseClick = (route) => {
    if (ENCOUNTER_ROUTES.has(route)) {
      if (encounterId) {
        navigate(`${route}/${encounterId}`);
      } else {
        // No active encounter — navigate to queue to start one.
        navigate('/queue');
      }
    } else {
      navigate(route);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#0b0d14]">
      {/* Top Bar */}
      <div className="h-12 bg-[#12141e] border-b border-[#1e2235] flex items-center justify-between px-6">
        {/* Left: Logo/Brand */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">CK</span>
          </div>
          <span className="text-white font-semibold text-sm">CollabKare</span>
        </div>

        {/* Center: Phase Navigation Tabs */}
        <div className="flex items-center gap-1">
          {visiblePhases.map((phase) => {
            const isActive = phase.id === currentPhaseId;
            const isCompleted = phase.id < currentPhaseId;

            return (
              <button
                key={phase.key}
                onClick={() => handlePhaseClick(phase.route)}
                className={`
                  px-3 py-1 rounded text-xs font-medium transition-colors
                  ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : isCompleted
                        ? 'bg-green-600 text-white'
                        : 'text-gray-400 hover:text-gray-300'
                  }
                `}
              >
                {phase.label}
              </button>
            );
          })}
        </div>

        {/* Right: Role chip + Avatar */}
        <div className="flex items-center gap-4">
          {/* Role display */}
          <div className="flex items-center gap-2 border border-[#1e2235] rounded px-3 py-1">
            <Users className="w-4 h-4 text-gray-400" />
            {import.meta.env.DEV ? (
              /* Dev-only role switcher — hidden in prod builds */
              <select
                value={devRoleOverride ?? effectiveRole}
                onChange={(e) => setDevRoleOverride(e.target.value)}
                className="text-xs font-medium bg-transparent text-blue-300 cursor-pointer outline-none"
                title="DEV ONLY — switch role view"
              >
                {DEV_ROLES.map((r) => (
                  <option key={r} value={r} className="bg-[#12141e] text-white">
                    {ROLE_LABELS[r] ?? r}
                  </option>
                ))}
              </select>
            ) : (
              /* Prod — read-only role chip */
              <span className="text-xs font-medium text-blue-400">
                {ROLE_LABELS[effectiveRole] ?? effectiveRole}
              </span>
            )}
          </div>

          {/* Avatar */}
          <Avatar
            name={user?.name ?? 'User'}
            size="sm"
            color="bg-blue-600"
          />
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
